import { useEffect, useId, useRef } from 'react';
import { motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  icon?: LucideIcon;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  className?: string;
  size?: 'sm' | 'md';
  /** Opções com largura igual preenchendo a linha (útil no mobile). */
  fill?: boolean;
}

/** Controle segmentado Glass com pílula deslizante (layout animation). */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  className,
  size = 'md',
  fill = false,
}: SegmentedControlProps<T>) {
  const layoutId = useId();
  const activeRef = useRef<HTMLButtonElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Com `fill`, todas as opções já cabem — scrollIntoView no documento
  // desloca o dialog/teclado no mobile e prende o foco no input errado.
  useEffect(() => {
    if (fill) return;
    const scroller = scrollerRef.current;
    const active = activeRef.current;
    if (!scroller || !active) return;
    const scrollerRect = scroller.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const left = activeRect.left - scrollerRect.left + scroller.scrollLeft;
    const target =
      left - scroller.clientWidth / 2 + active.clientWidth / 2;
    scroller.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [value, fill]);

  return (
    <div
      ref={scrollerRef}
      className={cn(
        'glass-thin thin-scrollbar rounded-xl p-1',
        fill
          ? 'flex w-full'
          : 'inline-flex max-w-full overflow-x-auto',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            ref={active ? activeRef : undefined}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              'relative inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              size === 'sm' ? 'px-2.5 py-2 text-xs sm:px-3' : 'px-3.5 py-1.5 text-sm',
              fill ? 'min-w-0 flex-1' : 'shrink-0',
              active ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {active && (
              <motion.span
                layoutId={layoutId}
                className="absolute inset-0 rounded-lg bg-primary shadow-soft"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              />
            )}
            {Icon ? <Icon className="relative size-4 shrink-0" /> : null}
            <span className="relative truncate">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
